'use client';

import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { BiEdit, BiTrash } from 'react-icons/bi';
import { BsFiletypeCsv, BsSearch } from 'react-icons/bs';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useSearchParams } from 'react-router-dom';
import useExportCSV from '../../hooks/useExportCSV';
import useShowModal from '../../hooks/useShowModal';
import Modal from '../modal/Modal';

interface Props<T extends Record<string, any>> {
  data: T[];
  config: {
    headers: { key: string; label: string }[];
  };
  showExportButton?: boolean;
  onEdit?: () => void;
  title?: string;
  selectCheckbox?: boolean;
  showActions?: boolean;
  allDataUrl?: string;
  width?: string;
  dataFilter?: boolean;
  deleteAction?: (userId: number | string) => void;
  enableSorting?: boolean;
  rowsPerPage?: number;
  customCSVExporting?: any;
  // Server-side pagination props (optional - backwards compatible)
  serverSideMode?: boolean;
  totalCount?: number;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  onSortChange?: (sortBy: string) => void;
  showSearch?: boolean;
  rowActions?: (row: T) => React.ReactNode;
  actionsLabel?: string;
}

const Table = <T extends Record<string, any>>({
  data,
  config,
  showExportButton,
  onEdit,
  title,
  selectCheckbox,
  dataFilter = false,
  showActions,
  width,
  deleteAction,
  enableSorting = true,
  rowsPerPage = 25,
  customCSVExporting,
  // Server-side pagination props
  serverSideMode = false,
  totalCount = 0,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  onSortChange,
  showSearch = true,
  rowActions,
  actionsLabel = 'Action',
}: Props<T>) => {
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'ascending' | 'descending' | 'none';
  } | null>(null);
  const [dateFilter, setDateFilter] = useState<string>('1 year');
  const [customDateRange, setCustomDateRange] = useState<{
    from: Date;
    to: Date;
  }>({
    from: new Date('2000-01-01'),
    to: new Date('3073-12-31'),
  });
  // const [isLoading, setIsLoading] = useState(false);
  const [isForCSVLoading, setIsForCSVLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [localCurrentPage, setLocalCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const handleExportCSV = useExportCSV();
  const { closeModal, showModal, openModal } = useShowModal();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleDateFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setDateFilter(e.target.value);
    if (!serverSideMode) {
      setLocalCurrentPage(1); // Reset to first page when filter changes (client-side only)
    }
  };

  const handleCustomDateChange = (from: Date | null, to: Date | null) => {
    setCustomDateRange({
      from: from || new Date('2000-01-01'),
      to: to || new Date('3023-12-31'),
    });
    if (!serverSideMode) {
      setLocalCurrentPage(1); // Reset to first page when date range changes (client-side only)
    }
  };

  const filteredData = React.useMemo(() => {
    const filterDate = (dateStr: string): Date => {
      if (!dateStr) return new Date(); // Fallback to the current date if dateStr is empty or invalid
      const [day, month, year] = dateStr.split('/').map(Number);
      if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
        return new Date(year, month - 1, day);
      }
      return new Date(); // Fallback to the current date if parsing fails
    };

    let filtered = [...data];

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(item => {
        return Object.values(item).some(value =>
          String(value).toLowerCase().includes(searchQuery.toLowerCase())
        );
      });
    }

    if (dateFilter !== 'Custom') {
      const dateThreshold = new Date();
      switch (dateFilter) {
        case '3 months':
          dateThreshold.setMonth(dateThreshold.getMonth() - 3);
          break;
        case '6 months':
          dateThreshold.setMonth(dateThreshold.getMonth() - 6);
          break;
        case '9 months':
          dateThreshold.setMonth(dateThreshold.getMonth() - 9);
          break;
        case '1 year':
          dateThreshold.setFullYear(dateThreshold.getFullYear() - 1);
          break;
        default:
          break;
      }

      if (filterDate) {
        filtered = filtered.filter(item => {
          const itemDate = filterDate(item.date);
          return itemDate >= dateThreshold;
        });
      }
    } else if (customDateRange.from && customDateRange.to) {
      filtered = filtered.filter(item => {
        const itemDate = filterDate(item.date);
        return (
          itemDate >= customDateRange.from && itemDate <= customDateRange.to
        );
      });
    }

    return filtered;
  }, [data, dateFilter, customDateRange, searchQuery]);

  const confirmDelete = () => {
    if (selectedRow) {
      if (showActions) deleteAction?.(selectedRow);
      setSelectedRow(null);
    }
    closeModal();
  };

  const sortedData = React.useMemo(() => {
    if (!enableSorting) return filteredData;

    if (sortConfig !== null && sortConfig.direction !== 'none') {
      return [...filteredData].sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }

    return filteredData;
  }, [filteredData, sortConfig, enableSorting]);

  // Pagination logic - different for server-side vs client-side
  const finalTotalPages = serverSideMode
    ? totalPages
    : Math.ceil(sortedData.length / rowsPerPage);
  const finalCurrentPage = serverSideMode ? currentPage : localCurrentPage;

  const paginatedData = React.useMemo(() => {
    if (serverSideMode) {
      // For server-side pagination, use data as-is (already paginated by server)
      return data;
    } else {
      // For client-side pagination, slice the sorted data
      const startIndex = (localCurrentPage - 1) * rowsPerPage;
      return sortedData.slice(startIndex, startIndex + rowsPerPage);
    }
  }, [serverSideMode, data, sortedData, localCurrentPage, rowsPerPage]);

  const handlePageChange = (page: number) => {
    if (serverSideMode && onPageChange) {
      onPageChange(page);
    } else {
      setLocalCurrentPage(page);
    }
  };

  const handleSort = (key: string) => {
    if (serverSideMode && onSortChange) {
      // For server-side sorting, just call the callback
      onSortChange(key);
    } else {
      // For client-side sorting, manage state locally
      let direction: 'ascending' | 'descending' | 'none' = 'ascending';
      if (sortConfig && sortConfig.key === key) {
        if (sortConfig.direction === 'ascending') {
          direction = 'descending';
        } else if (sortConfig.direction === 'descending') {
          direction = 'none';
        }
      }
      setSortConfig({ key, direction });
    }
  };

  const handleEdit = (email: string) => {
    setSearchParams({ ...Object.fromEntries(searchParams), email: email });
    onEdit?.();
  };

  const exportCSV = async (forCSV = false) => {
    if (forCSV) {
      setIsForCSVLoading(true);
      await handleExportCSV(config, data, title);
      setIsForCSVLoading(false);
    } else {
      // Handle regular fetch if needed
    }
  };

  if (data.length === 0)
    return <div className='text-center py-10'>No data available</div>;

  return (
    <div className='relative sm:rounded-lg rounded-lg border'>
      {/* New Header Component */}
      <div className='bg-primary w-full flex flex-col sm:flex-row sm:items-center justify-between rounded-t-lg px-4 py-3 gap-3'>
        <div className='flex items-center gap-x-4 flex-wrap flex-1 min-w-0'>
          <h2 className='text-white font-semibold text-xl leading-none'>
            {title}
          </h2>
          {showSearch && (
            <div className='bg-white rounded-md flex items-center w-full sm:w-72'>
              <BsSearch className='ml-3 text-gray-500' />
              <input
                type='text'
                placeholder='Search...'
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className='pl-3 pr-1 py-2 rounded-md focus:outline-none'
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className='ml-2 text-gray-500 hover:text-gray-700'
                >
                  ✕
                </button>
              )}
            </div>
          )}
        </div>
        {showExportButton && (
          <div className='flex items-center shrink-0'>
            <button
              onClick={async () => {
                if (customCSVExporting) {
                  setIsForCSVLoading(true);
                  await customCSVExporting(true);
                  setIsForCSVLoading(false);
                } else {
                  exportCSV(true);
                }
              }}
              className='rounded-lg text-black border flex px-4 py-1.5 items-center justify-center bg-white h-9'
              disabled={isForCSVLoading}
            >
              <span>{isForCSVLoading ? 'Exporting...' : 'Export CSV'}</span>
              <BsFiletypeCsv className='ml-2' size={20} />
            </button>
          </div>
        )}
      </div>

      {/* Date Filter Section */}
      {dataFilter && (
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-4 p-4 bg-gray-50'>
          <div className='flex items-center gap-2 text-nowrap'>
            <label>Date Filter:</label>
            <select
              value={dateFilter}
              onChange={handleDateFilterChange}
              className='border rounded p-2 w-40'
            >
              <option value='3 months'>Last 3 months</option>
              <option value='6 months'>Last 6 months</option>
              <option value='9 months'>Last 9 months</option>
              <option value='1 year'>Last 1 year</option>
              <option value='Custom'>Custom</option>
            </select>
          </div>

          {dateFilter === 'Custom' && (
            <div className='flex items-center'>
              <div className='flex items-center mr-4'>
                <label className='mr-2'>From:</label>
                <DatePicker
                  selected={customDateRange.from}
                  onChange={date =>
                    handleCustomDateChange(date, customDateRange.to)
                  }
                  dateFormat='dd/MM/yyyy'
                  className='border rounded p-2'
                />
              </div>
              <div className='flex items-center'>
                <label className='mr-2'>To:</label>
                <DatePicker
                  selected={customDateRange.to}
                  onChange={date =>
                    handleCustomDateChange(customDateRange.from, date)
                  }
                  dateFormat='dd/MM/yyyy'
                  className='border rounded p-2'
                />
              </div>
            </div>
          )}
        </div>
      )}
      {/* {title && <p className='text-xl my-3 font-bold'>{title}</p>} */}

      <div className='overflow-x-auto w-full'>
        <table
          className={`${width ?? 'w-fit'
            } text-sm text-left rtl:text-right text-gray-500`}
        >
          <thead className='text-xs text-gray-700 bg-gray-50 sticky top-0 z-10'>
            <tr>
              {selectCheckbox && (
                <th scope='col' className='p-4 sticky top-0 bg-gray-50'>
                  <div className='flex items-center'>
                    <input
                      id='checkbox-all-search'
                      type='checkbox'
                      className='w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2'
                    />
                    <label htmlFor='checkbox-all-search' className='sr-only'>
                      checkbox
                    </label>
                  </div>
                </th>
              )}
              {config.headers.map(header => (
                <th
                  key={String(header.key)}
                  scope='col'
                  className={`px-6 py-3 sticky top-0 bg-gray-50 ${enableSorting ? 'cursor-pointer' : ''
                    }`}
                  onClick={
                    enableSorting ? () => handleSort(header.key) : undefined
                  }
                >
                  <div className='flex items-center justify-start gap-2'>
                    <span>{header.label}</span>
                    {enableSorting && (
                      <span>
                        {sortConfig?.key === header.key
                          ? sortConfig.direction === 'ascending'
                            ? ' ↑'
                            : sortConfig.direction === 'descending'
                              ? ' ↓'
                              : ' ↕'
                          : ' ↕'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
              {showActions && (
                <th scope='col' className='px-6 py-3 sticky top-0 bg-gray-50'>
                  Action
                </th>
              )}
              {rowActions && (
                <th scope='col' className='px-6 py-3 sticky top-0 bg-gray-50'>
                  {actionsLabel}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className='bg-white border-b hover:bg-gray-50'
                onMouseEnter={() => setSelectedRow(row.id)}
              >
                {selectCheckbox && (
                  <td className='w-4 p-4'>
                    <div className='flex items-center'>
                      <input
                        id={`checkbox-${rowIndex}`}
                        type='checkbox'
                        className='w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2'
                      />
                      <label
                        htmlFor={`checkbox-${rowIndex}`}
                        className='sr-only'
                      >
                        checkbox
                      </label>
                    </div>
                  </td>
                )}
                {config.headers.map(header => (
                  <td
                    key={String(header.key)}
                    className='px-6 py-4 max-w-32 truncate'
                  >
                    {row[header.key]}
                  </td>
                ))}
                {showActions && (
                  <td className='px-6 py-4 text-right flex gap-2 items-center'>
                    <button
                      className='text-primary'
                      onClick={() => handleEdit(row.email)}
                    >
                      <BiEdit size={20} />
                    </button>
                    <button className='text-red-600' onClick={openModal}>
                      <BiTrash size={20} />
                    </button>
                  </td>
                )}
                {rowActions && (
                  <td className='px-6 py-4'>{rowActions(row)}</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {finalTotalPages > 1 && (
        <div className='flex items-center justify-between bg-white px-4 py-3 sm:px-6'>
          <div className='flex flex-1 justify-between sm:hidden'>
            <button
              onClick={() =>
                handlePageChange(Math.max(1, finalCurrentPage - 1))
              }
              disabled={finalCurrentPage === 1}
              className={`relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium ${finalCurrentPage === 1
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-gray-700 hover:bg-gray-50'
                }`}
            >
              Previous
            </button>
            <button
              onClick={() =>
                handlePageChange(
                  Math.min(finalTotalPages, finalCurrentPage + 1)
                )
              }
              disabled={finalCurrentPage === finalTotalPages}
              className={`relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium ${finalCurrentPage === finalTotalPages
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-gray-700 hover:bg-gray-50'
                }`}
            >
              Next
            </button>
          </div>
          <div className='hidden sm:flex sm:flex-1 sm:items-center sm:justify-between'>
            <div>
              <p className='text-sm text-gray-700'>
                Showing{' '}
                <span className='font-medium'>
                  {serverSideMode
                    ? (finalCurrentPage - 1) * rowsPerPage + 1
                    : (finalCurrentPage - 1) * rowsPerPage + 1}
                </span>{' '}
                to{' '}
                <span className='font-medium'>
                  {serverSideMode
                    ? Math.min(finalCurrentPage * rowsPerPage, totalCount)
                    : Math.min(
                      finalCurrentPage * rowsPerPage,
                      sortedData.length
                    )}
                </span>{' '}
                of{' '}
                <span className='font-medium'>
                  {serverSideMode ? totalCount : sortedData.length}
                </span>{' '}
                results
              </p>
            </div>
            <div>
              <nav
                className='isolate inline-flex -space-x-px rounded-md shadow-sm'
                aria-label='Pagination'
              >
                <button
                  onClick={() =>
                    handlePageChange(Math.max(1, finalCurrentPage - 1))
                  }
                  disabled={finalCurrentPage === 1}
                  className={`relative inline-flex items-center rounded-l-md px-2 py-2 ${finalCurrentPage === 1
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-400'
                    }`}
                >
                  <span className='sr-only'>Previous</span>
                  <FaChevronLeft
                    className={`${finalCurrentPage !== 1 ? 'hover:text-gray-500' : ''
                      }`}
                  />
                </button>

                {/* Page numbers */}
                {Array.from(
                  { length: Math.min(5, finalTotalPages) },
                  (_, i) => {
                    // Show pages around current page
                    let pageNum: number;
                    if (finalTotalPages <= 5) {
                      pageNum = i + 1;
                    } else if (finalCurrentPage <= 3) {
                      pageNum = i + 1;
                    } else if (finalCurrentPage >= finalTotalPages - 2) {
                      pageNum = finalTotalPages - 4 + i;
                    } else {
                      pageNum = finalCurrentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${finalCurrentPage === pageNum
                          ? 'z-10 bg-primary text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'
                          : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-offset-0'
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  }
                )}

                <button
                  onClick={() =>
                    handlePageChange(
                      Math.min(finalTotalPages, finalCurrentPage + 1)
                    )
                  }
                  disabled={finalCurrentPage === finalTotalPages}
                  className={`relative inline-flex items-center rounded-r-md px-2 py-2 ${finalCurrentPage === finalTotalPages
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-400'
                    }`}
                >
                  <span className='sr-only'>Next</span>
                  <FaChevronRight
                    className={`${finalCurrentPage !== finalTotalPages
                      ? 'hover:text-gray-500'
                      : ''
                      }`}
                  />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {showModal && <Modal onClose={closeModal} />}
      <Modal
        isOpen={showModal}
        onClose={closeModal}
        showCloseIcon={false}
        title='Are you sure you want to delete this user?'
      >
        <div className='flex items-center justify-center'>
          <button
            className='bg-red-700 px-7 py-1 text-white rounded'
            onClick={confirmDelete}
          >
            Yes
          </button>
          <button
            className='bg-white px-5 py-1 text-pretty'
            onClick={closeModal}
          >
            Cancel
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default Table;
